import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { cn, formatDate, getRecordTypeLabel, getInspectionTypeLabel } from '../lib/utils';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  ClipboardCheck,
  Award,
  Target,
  Loader2,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6366f1', '#8b5cf6'];
const RECORD_TYPE_COLORS = {
  car: '#3b82f6',
  '3w': '#8b5cf6',
  bus: '#f59e0b',
  truck: '#10b981',
  trailer: '#ef4444',
  cng_storage: '#6366f1',
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border rounded-lg shadow-lg text-sm">
        <p className="font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function InspectorPerformancePage() {
  const { user, isAdmin, isRegulator, isInspector } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [performance, setPerformance] = useState(null);
  const [allInspectors, setAllInspectors] = useState(null);
  const [monthlyTrend, setMonthlyTrend] = useState(null);
  const [selectedInspectorId, setSelectedInspectorId] = useState(null);

  useEffect(() => {
    loadPerformanceData();
  }, [period, selectedInspectorId]);

  const loadPerformanceData = async () => {
    setLoading(true);
    try {
      const [perfRes, monthlyRes] = await Promise.all([
        dashboardAPI.getInspectorPerformance(selectedInspectorId, parseInt(period)),
        dashboardAPI.getInspectorMonthlyTrend(selectedInspectorId, 6),
      ]);
      setPerformance(perfRes.data);
      setMonthlyTrend(monthlyRes.data);

      // Load all inspectors comparison for Admin/Regulator
      if (isAdmin || isRegulator) {
        const allRes = await dashboardAPI.getAllInspectorsComparison(parseInt(period));
        setAllInspectors(allRes.data);
      }
    } catch (err) {
      console.error('Failed to load performance data:', err);
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  // Prepare pie chart data for record types
  const recordTypePieData = performance ? 
    Object.entries(performance.by_record_type || {}).map(([type, count]) => ({
      name: getRecordTypeLabel(type),
      value: count,
      color: RECORD_TYPE_COLORS[type] || '#94a3b8'
    })) : [];

  // Prepare inspection type data
  const inspectionTypeData = performance ?
    Object.entries(performance.by_inspection_type || {}).map(([type, data]) => ({
      type: getInspectionTypeLabel(type),
      total: data.total,
      valid: data.valid,
      failed: data.failed,
      passRate: data.total > 0 ? Math.round((data.valid / data.total) * 100) : 0
    })) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="inspector-performance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isInspector ? 'My Performance' : 'Inspector Performance'}
          </h1>
          <p className="text-muted-foreground">
            {isInspector 
              ? 'Track your inspection metrics and trends'
              : 'Monitor inspector activity and performance metrics'
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {(isAdmin || isRegulator) && allInspectors?.inspectors?.length > 0 && (
            <Select value={selectedInspectorId || 'all'} onValueChange={(v) => setSelectedInspectorId(v === 'all' ? null : v)}>
              <SelectTrigger className="w-[200px]" data-testid="inspector-select">
                <SelectValue placeholder="All Inspectors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Inspectors</SelectItem>
                {allInspectors.inspectors.map((insp) => (
                  <SelectItem key={insp.inspector_id} value={insp.inspector_id}>
                    {insp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Inspector Info (if specific inspector selected) */}
      {performance?.inspector && (
        <Card className="border-l-4 border-l-primary">
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{performance.inspector.name}</h3>
                <p className="text-sm text-muted-foreground">
                  {performance.inspector.business_name} • {performance.inspector.region} • License: {performance.inspector.license_number}
                </p>
              </div>
              <Badge variant={performance.inspector.status === 'active' ? 'default' : 'secondary'} className="ml-auto">
                {performance.inspector.status}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card data-testid="total-inspections-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Inspections</CardTitle>
            <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{performance?.summary?.total_inspections || 0}</div>
            <p className="text-xs text-muted-foreground">
              ~{performance?.summary?.avg_per_day || 0} per day average
            </p>
          </CardContent>
        </Card>

        <Card data-testid="pass-rate-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pass Rate</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              {performance?.summary?.pass_rate || 0}%
            </div>
            <Progress 
              value={performance?.summary?.pass_rate || 0} 
              className="h-2 mt-2"
            />
          </CardContent>
        </Card>

        <Card data-testid="valid-inspections-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valid Inspections</CardTitle>
            <CheckCircle className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              {performance?.summary?.valid || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Approved and active
            </p>
          </CardContent>
        </Card>

        <Card data-testid="failed-inspections-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed Inspections</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {performance?.summary?.failed || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {performance?.summary?.fail_rate || 0}% fail rate
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Daily Trend Chart */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Daily Inspection Trend</CardTitle>
            <CardDescription>Inspections per day over the selected period</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {performance?.daily_trend?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={performance.daily_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => v.slice(5)} // Show MM-DD
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line type="monotone" dataKey="total" stroke="#3b82f6" name="Total" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="valid" stroke="#10b981" name="Valid" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="failed" stroke="#ef4444" name="Failed" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Record Type Distribution */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Inspections by Vehicle Type</CardTitle>
            <CardDescription>Distribution across different vehicle types</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {recordTypePieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={recordTypePieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {recordTypePieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inspection Type Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Performance by Inspection Type</CardTitle>
          <CardDescription>Breakdown of inspections by type with pass rates</CardDescription>
        </CardHeader>
        <CardContent>
          {inspectionTypeData.length > 0 ? (
            <div className="space-y-4">
              {inspectionTypeData.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-32 text-sm font-medium truncate">{item.type}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Progress value={item.passRate} className="h-2 flex-1" />
                      <span className="text-sm font-medium w-12 text-right">{item.passRate}%</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>Total: {item.total}</span>
                      <span className="text-emerald-600">Valid: {item.valid}</span>
                      <span className="text-red-600">Failed: {item.failed}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No inspection type data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monthly Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Monthly Trend (Last 6 Months)</CardTitle>
          <CardDescription>Long-term inspection volume and success trends</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {monthlyTrend?.trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyTrend.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="valid" fill="#10b981" name="Valid" stackId="a" />
                  <Bar dataKey="failed" fill="#ef4444" name="Failed" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No monthly data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* All Inspectors Comparison Table (Admin/Regulator only) */}
      {(isAdmin || isRegulator) && allInspectors && !selectedInspectorId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-500" />
              Inspector Leaderboard
            </CardTitle>
            <CardDescription>
              Performance comparison across all inspectors for the selected period
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Inspector</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Valid</TableHead>
                  <TableHead className="text-right">Failed</TableHead>
                  <TableHead className="text-right">Pass Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allInspectors.inspectors.map((insp, idx) => (
                  <TableRow 
                    key={insp.inspector_id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedInspectorId(insp.inspector_id)}
                    data-testid={`inspector-row-${insp.inspector_id}`}
                  >
                    <TableCell className="font-medium">
                      {idx === 0 && <Award className="h-4 w-4 text-amber-500 inline mr-1" />}
                      {idx + 1}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{insp.name}</p>
                        <p className="text-xs text-muted-foreground">{insp.business_name}</p>
                      </div>
                    </TableCell>
                    <TableCell>{insp.region || '-'}</TableCell>
                    <TableCell className="text-right font-medium">{insp.metrics.total}</TableCell>
                    <TableCell className="text-right text-emerald-600">{insp.metrics.valid}</TableCell>
                    <TableCell className="text-right text-red-600">{insp.metrics.failed}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant={insp.metrics.pass_rate >= 90 ? 'default' : insp.metrics.pass_rate >= 70 ? 'secondary' : 'destructive'}>
                        {insp.metrics.pass_rate}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
